import React from 'react';

/**
 * ErrorBoundary global — captura erros de render/lifecycle dos filhos e
 * mostra um fallback recuperável, evitando "tela branca" fatal.
 *
 * AUTO-RECOVERY para erros não-fatais conhecidos:
 *  - "insertBefore" / "removeChild" / "NotFoundError" em Node → DOM foi
 *    mutilado por extensão do browser (Google Translate, Grammarly, Stylus,
 *    Honey, etc). O estado React continua válido; basta re-renderizar.
 *    Tentamos recuperar até 5x num intervalo curto. Se persistir, mostra
 *    fallback com instrução para o usuário desabilitar extensões/tradução.
 *
 * Erros realmente fatais (e.g., TypeError em código nosso) mostram o
 * fallback imediato.
 */
const NON_FATAL_PATTERNS = [
  /insertBefore/i,
  /removeChild/i,
  /failed to execute .* on .Node./i,
  /not a child of this node/i,
  /não é filho deste nó/i,
  /NotFoundError/i,
];

function isNonFatalDomError(err) {
  if (!err) return false;
  const msg = String(err?.message || err);
  return NON_FATAL_PATTERNS.some((re) => re.test(msg));
}

const MAX_AUTO_RECOVER = 5;

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      info: null,
      recoverCount: 0,
      isNonFatal: false,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      isNonFatal: isNonFatalDomError(error),
    };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    try {
      const log = {
        at: new Date().toISOString(),
        message: String(error?.message || error),
        stack: String(error?.stack || ''),
        componentStack: info?.componentStack || '',
        nonFatal: isNonFatalDomError(error),
      };
      localStorage.setItem('kaven_last_error', JSON.stringify(log));
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', log);
    } catch {}

    // Auto-recovery para erros DOM não-fatais. Espera um tick e re-renderiza.
    if (isNonFatalDomError(error) && this.state.recoverCount < MAX_AUTO_RECOVER) {
      setTimeout(() => {
        this.setState((prev) => ({
          hasError: false,
          error: null,
          info: null,
          recoverCount: prev.recoverCount + 1,
        }));
      }, 50);
    }
  }

  handleReload = () => {
    try { localStorage.removeItem('kaven_last_error'); } catch {}
    window.location.reload();
  };

  handleResetLocal = () => {
    if (!window.confirm(
      'Isso vai apagar TODOS os dados locais deste dispositivo (comandas, estoque, sessões de caixa).\n\n'
      + 'Use só se a tela continuar travada após recarregar. Confirma?'
    )) return;
    try {
      const req = indexedDB.deleteDatabase('KavenLocalDB');
      req.onsuccess = () => window.location.reload();
      req.onerror = () => window.location.reload();
      req.onblocked = () => window.location.reload();
    } catch {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = String(this.state.error?.message || this.state.error || 'Erro desconhecido');
    const isDomError = this.state.isNonFatal;

    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen w-full bg-slate-50 flex items-center justify-center p-6"
      >
        <div className="max-w-lg w-full bg-white border-2 border-rose-200 rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-rose-50 border-b border-rose-200 px-5 py-3">
            <h1 className="text-base font-black text-rose-800">
              Sistema travado
            </h1>
            <p className="text-xs text-rose-700 mt-0.5">
              {isDomError
                ? 'Uma extensão do navegador está interferindo no sistema.'
                : 'Encontrei um erro mas evitei a tela branca. Você pode recarregar e continuar.'}
            </p>
          </div>
          <div className="p-5 space-y-3">
            {isDomError && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 space-y-2">
                <p className="text-xs font-black text-amber-900">
                  AÇÃO NECESSÁRIA — Provável causa:
                </p>
                <ol className="text-xs text-amber-900 space-y-1 list-decimal pl-4">
                  <li>Tradução automática do navegador está ligada (Chrome / Edge → Traduzir para Português). <strong>Desligue.</strong></li>
                  <li>Extensões instaladas (Grammarly, Honey, Stylus, Dark Reader, etc) podem estar mexendo no site. Teste em <strong>aba anônima</strong> sem extensões.</li>
                  <li>Se o problema persistir, recarregue e clique em "Limpar dados locais".</li>
                </ol>
              </div>
            )}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                Detalhe técnico
              </p>
              <p className="font-mono text-xs text-slate-700 break-all">
                {msg}
              </p>
            </div>
            <button
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReload}
              className="w-full h-11 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors"
            >
              Recarregar agora
            </button>
            <button
              data-testid="error-boundary-reset-btn"
              onClick={this.handleResetLocal}
              className="w-full h-10 rounded-xl border-2 border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors"
            >
              Travou de novo? Limpar dados locais e recarregar
            </button>
            <p className="text-[10px] text-slate-400 text-center pt-1">
              Seus dados ficam salvos no Firebase quando o sync está ativo.
            </p>
          </div>
        </div>
      </div>
    );
  }
}
